import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { equipmentApi } from './service';
import type { EquipmentCreateValues, EquipmentUpdateValues } from './schema';
import type { Equipment } from './types';

/**
 * Query key factory.
 *
 *   equipmentKeys.all                 every cached equipment query
 *   equipmentKeys.lists()             all list queries
 *   equipmentKeys.list({ vesselId })  list filtered by vessel (URL filter)
 *   equipmentKeys.tree(vesselId)      hierarchical tree query
 *   equipmentKeys.detail(id)          single equipment record
 */
export const equipmentKeys = {
  all: ['equipment'] as const,
  lists: () => [...equipmentKeys.all, 'list'] as const,
  list: (filters: { vesselId?: string }) =>
    [...equipmentKeys.lists(), filters] as const,
  tree: (vesselId: string) =>
    [...equipmentKeys.all, 'tree', vesselId] as const,
  detail: (id: string) => [...equipmentKeys.all, 'detail', id] as const,
};

export function useEquipment({ vesselId }: { vesselId?: string } = {}) {
  return useQuery({
    queryKey: equipmentKeys.list({ vesselId }),
    queryFn: () => equipmentApi.list({ vesselId }),
  });
}

export function useEquipmentTree(vesselId: string | undefined) {
  return useQuery({
    queryKey: vesselId ? equipmentKeys.tree(vesselId) : equipmentKeys.all,
    queryFn: () => equipmentApi.tree(vesselId as string),
    enabled: Boolean(vesselId),
  });
}

export function useEquipmentItem(id: string | undefined) {
  return useQuery({
    queryKey: id ? equipmentKeys.detail(id) : equipmentKeys.all,
    queryFn: () => equipmentApi.getById(id as string),
    enabled: Boolean(id),
  });
}

/**
 * Mutations — same plain-invalidation pattern: every mutation
 * invalidates every list (irrespective of filter) plus the tree of
 * the affected vessel and the single-record detail. No optimistic
 * updates yet; offline-aware variants land in F12.
 */
export function useCreateEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EquipmentCreateValues) => equipmentApi.create(input),
    onSuccess: (created: Equipment) => {
      qc.invalidateQueries({ queryKey: equipmentKeys.lists() });
      qc.invalidateQueries({ queryKey: equipmentKeys.tree(created.vesselId) });
      qc.setQueryData(equipmentKeys.detail(created.id), created);
    },
  });
}

export function useUpdateEquipment(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: EquipmentUpdateValues) =>
      equipmentApi.update(id, patch),
    onSuccess: (updated: Equipment) => {
      qc.invalidateQueries({ queryKey: equipmentKeys.lists() });
      qc.invalidateQueries({ queryKey: equipmentKeys.tree(updated.vesselId) });
      qc.setQueryData(equipmentKeys.detail(id), updated);
    },
  });
}

export function useDeleteEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => equipmentApi.remove(id),
    onSuccess: (_void, id) => {
      qc.invalidateQueries({ queryKey: equipmentKeys.all });
      qc.removeQueries({ queryKey: equipmentKeys.detail(id) });
    },
  });
}
